import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Bell, MessageSquare, Calendar, Baby, Contact, History,
  UserPlus, Smartphone, CheckCircle,
} from 'lucide-react';
// Parents features: 1=attendance, 2=messages, 3=calendar, 4=contacts, 5=history (removed: child profile)
import SectionWrapper from '../../components/marketing/SectionWrapper';
import FeatureCard from '../../components/marketing/FeatureCard';
import ScreenshotShowcase from '../../components/marketing/ScreenshotShowcase';
import CTABanner from '../../components/marketing/CTABanner';
import { ALL_PHOTOS } from '../../components/marketing/photoPool';

export default function ParentsPage() {
  const { t } = useTranslation('marketing');

  const features = [
    { icon: Bell, title: t('parents.feature1_title'), description: t('parents.feature1_desc'), color: '#16a34a' },
    { icon: MessageSquare, title: t('parents.feature2_title'), description: t('parents.feature2_desc'), color: '#0891b2' },
    { icon: Calendar, title: t('parents.feature3_title'), description: t('parents.feature3_desc'), color: '#ea580c' },
    { icon: Contact, title: t('parents.feature4_title'), description: t('parents.feature4_desc'), color: '#dc2626' },
    { icon: History, title: t('parents.feature5_title'), description: t('parents.feature5_desc'), color: '#0d9488' },
  ];

  const steps = [
    { icon: UserPlus, title: t('parents.step1_title'), desc: t('parents.step1_desc'), color: '#0891b2' },
    { icon: Smartphone, title: t('parents.step2_title'), desc: t('parents.step2_desc'), color: '#16a34a' },
    { icon: CheckCircle, title: t('parents.step3_title'), desc: t('parents.step3_desc'), color: '#ea580c' },
  ];

  return (
    <>
      <section className="pt-24 pb-14 bg-gradient-to-br from-emerald-50/60 via-white to-green-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-green-50 border border-green-100 text-green-700 text-xs font-semibold tracking-wide uppercase mb-6">
              <Baby className="w-3.5 h-3.5" />
              {t('parents.section_title')}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">{t('parents.title')}</h1>
            <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto">{t('parents.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <SectionWrapper background="white">
        <ScreenshotShowcase
          imageSrcs={ALL_PHOTOS}
          alt="Parent mobile app"
          direction="left"
          title={t('parents.feature1_title')}
          subtitle={t('parents.feature1_desc')}
          features={[
            { text: t('parents.feature2_title') + ' \u2014 ' + t('parents.feature2_desc') },
            { text: t('parents.feature3_title') + ' \u2014 ' + t('parents.feature3_desc') },
            { text: t('parents.feature5_title') + ' \u2014 ' + t('parents.feature5_desc') },
          ]}
          accentColor="#16a34a"
          placeholderIcon={Smartphone}
        />
      </SectionWrapper>

      <SectionWrapper background="gray">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {features.map((f, i) => (
            <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} color={f.color} index={i} />
          ))}
        </div>
      </SectionWrapper>

      <SectionWrapper title={t('parents.how_it_works')} background="white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="text-center"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: `${step.color}10` }}
              >
                <step.icon className="w-5 h-5" style={{ color: step.color }} />
              </div>
              <div className="text-xl font-bold text-gray-200 mb-1.5">{i + 1}</div>
              <h3 className="text-[15px] font-bold text-gray-900 mb-1">{step.title}</h3>
              <p className="text-gray-500 text-[13px]">{step.desc}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mt-6">{t('parents.step1_note')}</p>
      </SectionWrapper>

      <CTABanner
        title={t('cta.title')}
        subtitle={t('cta.subtitle')}
        buttonText={t('cta.button')}
      />
    </>
  );
}
